import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CartesianChartSeries, EChartOption } from '@siemens/charts-ng';
import { Track, TrackDataSet } from 'src/app/main/shared/DataTypes';
import { SettingsService } from 'src/app/main/shared/services/settings.service';

import { TrackParameterName, TrackService } from '../../track.service';

// Chart configuration constants
export const CHART_CONSTANTS = {
  CURVE: {
    MIN_CURVATURE: 0.0001, // 1 / 10000m radius
    MIN_RADIUS: 10,
    MAX_RADIUS: 10000
  },
  STATION: {
    LABEL_ROTATE: 25,
    LABEL_DISTANCE: 40,
    LABEL_FONT_SIZE: 11,
    SYMBOL_SIZE: 9
  },
  POINT: {
    SYMBOL_SIZE: 6,
    SYMBOL_SIZE_WARNING: 9
  },
  GRID: {
    BOTTOM_PADDING: 80
  }
};

// Snap step configuration for editing
export const SNAP_STEPS: Partial<Record<TrackParameterName, number>> = {
  maximum_speed_data: 5,
  elevation_data: 1,
  tunnel_factor_data: 0.1,
  network_power_data: 10,
  charging_station_data: 1,
  hydrogen_refuel_station_data: 1,
  curve_data: 1,
  curve_radius_data_graz: 0.0001
};

@Injectable({
  providedIn: 'root'
})
export class TrackChartBuilderService {
  private trackService = inject(TrackService);
  private translateService = inject(TranslateService);
  private settingsService = inject(SettingsService);

  /**
   * Main entry point to build chart options for a given track parameter
   */
  buildChartOptions(track: Track, dataKey: TrackParameterName): EChartOption | null {
    const parameterInfo = this.trackService.parameterNameMappings[dataKey];
    if (!parameterInfo) return null;

    const stationData: TrackDataSet[] = track.parameters['station_data'];
    const trackDataSets: TrackDataSet[] = track.parameters[dataKey];

    const xData = trackDataSets.map(data => data.position);
    const warningFlags = trackDataSets.map(data => data.warning || false);

    // Create coordinate pairs for y axis
    const chartData1 = trackDataSets.map(data => [data.position, data.trackParameter1]);
    const chartData2 =
      trackDataSets.map(data => [data.position, data['trackParameter2']]) || undefined;

    // Get translated labels
    const label1 = this.translateService.instant(
      'TRACK.COLUMN_NAMES.' + parameterInfo['trackParameter1'].name
    );
    const label2 = parameterInfo['trackParameter2']
      ? this.translateService.instant('TRACK.COLUMN_NAMES.' + parameterInfo['trackParameter2'].name)
      : undefined;

    // Create station labels mapping
    const stationLabels = stationData.map(data => ({
      value: data.position,
      name: data.trackParameter1
    }));

    // Build chart based on data type
    if (this.isStepChart(dataKey)) {
      return this.buildStepChart(
        dataKey,
        trackDataSets,
        chartData1,
        chartData2,
        parameterInfo,
        label1,
        label2,
        warningFlags,
        xData,
        stationLabels
      );
    } else if (dataKey === 'elevation_data') {
      return this.buildLineChart(
        trackDataSets,
        chartData1,
        chartData2,
        parameterInfo,
        label1,
        label2,
        warningFlags,
        xData,
        stationLabels
      );
    } else if (this.isBarChart(dataKey)) {
      return this.buildBarChart(
        dataKey,
        trackDataSets,
        chartData1,
        parameterInfo,
        label1,
        warningFlags,
        xData,
        stationLabels
      );
    }

    return null;
  }

  /**
   * Build step chart (for speed, curve, tunnel, network power)
   */
  private buildStepChart(
    dataKey: TrackParameterName,
    trackDataSets: TrackDataSet[],
    chartData1In: any[],
    chartData2: any[],
    parameterInfo: any,
    label1In: string,
    label2: string | undefined,
    warningFlags: boolean[],
    xData: any[],
    stationLabels: any[]
  ): EChartOption {
    let yAxisMin: number | undefined;
    let chartData1 = chartData1In;
    let label1 = label1In;

    // Handle curve data transformation
    if (dataKey === 'curve_data') {
      const yData = trackDataSets.map(data => data.trackParameter1);
      const curvatureData = yData.map((data: string | number) => {
        const num = typeof data === 'number' ? data : parseFloat(data);
        if (num === 0) return 0;
        return parseFloat((1 / num).toFixed(4));
      });

      chartData1 = trackDataSets.map((data, index) => [data.position, curvatureData[index]]);
      label1 = this.translateService.instant('TRACK.COLUMN_NAMES.curvature');
      parameterInfo.trackParameter1.unit = 'm⁻¹';
      yAxisMin = CHART_CONSTANTS.CURVE.MIN_CURVATURE;
    } else if (dataKey === 'network_power_data') {
      yAxisMin = this.calculateSmartYAxisMin(chartData1, chartData2);
    } else {
      yAxisMin = this.calculateSmartYAxisMin(chartData1);
    }

    const defaultOptions = this.createDefaultOptions(
      xData,
      stationLabels,
      trackDataSets,
      parameterInfo,
      label1,
      label2,
      dataKey
    );
    const stationSeries = this.createStationSeries(stationLabels, yAxisMin);

    const option: EChartOption = {
      ...defaultOptions,
      yAxis: this.buildYAxis(dataKey, parameterInfo, yAxisMin, chartData1),
      series: [
        {
          type: 'line',
          step: 'end',
          data:
            dataKey === 'curve_data'
              ? chartData1.map(point => [
                  point[0],
                  Math.max(CHART_CONSTANTS.CURVE.MIN_CURVATURE, point[1] as number)
                ])
              : chartData1,
          yAxisIndex: 0,
          name: label1,
          symbol: 'circle',
          symbolSize: this.getSymbolSize(warningFlags),
          itemStyle: this.getWarningAwareItemStyle(warningFlags)
        },
        // Add radius series for curve_data
        ...(dataKey === 'curve_data' ? [this.buildCurveRadiusSeries(trackDataSets)] : []),
        stationSeries as any
      ],
      legend: [{ left: '10', data: [label1] }]
    };

    // Add second series for network power
    if (dataKey === 'network_power_data' && label2) {
      option.series!.push({
        name: label2,
        type: 'line',
        step: 'end',
        data: chartData2,
        symbol: 'circle',
        symbolSize: this.getSymbolSize(warningFlags),
        itemStyle: this.getWarningAwareItemStyle(warningFlags)
      });
      option.legend!.push({ right: '10', data: [label2!] } as any);
    }

    return option;
  }

  // Build line chart (for elevation)

  private buildLineChart(
    trackDataSets: TrackDataSet[],
    chartData1: any[],
    chartData2: any[],
    parameterInfo: any,
    label1: string,
    label2: string | undefined,
    warningFlags: boolean[],
    xData: any[],
    stationLabels: any[]
  ): EChartOption {
    const yAxis1Min = this.calculateSmartYAxisMin(chartData1);
    const yAxis2Min = this.calculateSmartYAxisMin(chartData2);

    const defaultOptions = this.createDefaultOptions(
      xData,
      stationLabels,
      trackDataSets,
      parameterInfo,
      label1,
      label2,
      'elevation_data'
    );
    const stationSeries = this.createStationSeries(stationLabels, yAxis1Min);

    return {
      ...defaultOptions,
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: `{value} ${parameterInfo.trackParameter1.unit ?? ''}`,
            color: this.getThemeTextColor()
          },
          min: yAxis1Min
        },
        {
          type: 'value',
          axisLabel: {
            formatter: `{value} ${parameterInfo.trackParameter2.unit ?? ''}`,
            color: this.getThemeTextColor()
          },
          min: yAxis2Min
        }
      ],
      series: [
        {
          type: 'line',
          data: chartData1,
          yAxisIndex: 0,
          name: label1,
          symbol: 'circle',
          symbolSize: this.getSymbolSize(warningFlags),
          itemStyle: this.getWarningAwareItemStyle(warningFlags)
        },
        {
          type: 'line',
          data: chartData2,
          yAxisIndex: 1,
          name: label2,
          symbol: 'circle',
          symbolSize: this.getSymbolSize(warningFlags),
          itemStyle: this.getWarningAwareItemStyle(warningFlags)
        },
        stationSeries as any
      ],
      legend: [{ left: '10', data: [label1] }, ...(label2 ? [{ right: '10', data: [label2] }] : [])]
    };
  }

  // Build bar chart (for charging/hydrogen stations)

  private buildBarChart(
    dataKey: TrackParameterName,
    trackDataSets: TrackDataSet[],
    chartData1: any[],
    parameterInfo: any,
    label1: string,
    warningFlags: boolean[],
    xData: any[],
    stationLabels: any[]
  ): EChartOption {
    const yAxisMin = this.calculateSmartYAxisMin(chartData1);
    const defaultOptions = this.createDefaultOptions(
      xData,
      stationLabels,
      trackDataSets,
      parameterInfo,
      label1,
      undefined,
      dataKey
    );
    const stationSeries = this.createStationSeries(stationLabels, yAxisMin);

    return {
      ...defaultOptions,
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: `{value} ${parameterInfo.trackParameter1.unit ?? ''}`,
            color: this.getThemeTextColor()
          },
          min: yAxisMin
        }
      ],
      series: [
        {
          name: label1,
          type: 'bar',
          data: chartData1,
          barMaxWidth: 10,
          barMinWidth: 10,
          itemStyle: this.getWarningAwareItemStyle(warningFlags)
        },
        stationSeries as any
      ],
      legend: [{ left: '10', data: [label1] }]
    };
  }

  // Build Y-axis configuration

  private buildYAxis(
    dataKey: TrackParameterName,
    parameterInfo: any,
    yAxisMin: number,
    chartData1: any[]
  ): any {
    if (dataKey === 'curve_data') {
      return [
        {
          type: 'log',
          logBase: 10,
          min: CHART_CONSTANTS.CURVE.MIN_CURVATURE,
          max: 'dataMax',
          axisLabel: {
            formatter: (value: number) => {
              if (value === 0) return '0';
              return `${value.toFixed(4)} m⁻¹`;
            },
            color: this.getThemeTextColor()
          },
          name: this.translateService.instant('TRACK.COLUMN_NAMES.curvature') + ' (1/R)',
          nameLocation: 'middle',
          nameGap: 75,
          nameTextStyle: {
            color: this.getThemeTextColor()
          },
          splitLine: {
            show: true,
            lineStyle: { type: 'dashed', opacity: 0.3 }
          },
          minorTick: { show: true, splitNumber: 5 },
          minorSplitLine: { show: true, lineStyle: { opacity: 0.1 } }
        },
        {
          type: 'log',
          logBase: 10,
          min: 'dataMin',
          max: 10000,
          inverse: true,
          position: 'right',
          axisLabel: {
            formatter: (value: number) => {
              if (value >= 10000) return '≥10000m';
              return `${value}m`;
            },
            color: this.getThemeTextColor()
          },
          name: this.translateService.instant('TRACK.COLUMN_NAMES.curve_radius') + ' (R)',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            color: this.getThemeTextColor()
          },
          splitLine: { show: false }
        }
      ];
    }

    return [
      {
        type: 'value',
        axisLabel: {
          formatter: `{value} ${parameterInfo.trackParameter1.unit}`,
          color: this.getThemeTextColor()
        },
        min: yAxisMin,
        max:
          dataKey === 'maximum_speed_data'
            ? Math.max(...chartData1.map(point => point[1] as number)) + 20
            : undefined
      }
    ];
  }

  //Build curve radius series for right y-axis
  private buildCurveRadiusSeries(trackDataSets: TrackDataSet[]): any {
    return {
      type: 'line',
      step: 'end',
      areaStyle: null,
      data: trackDataSets.map(data => {
        const radius =
          typeof data.trackParameter1 === 'number'
            ? data.trackParameter1
            : parseFloat(data.trackParameter1);
        return [
          data.position,
          Math.min(
            CHART_CONSTANTS.CURVE.MAX_RADIUS,
            Math.max(CHART_CONSTANTS.CURVE.MIN_RADIUS, radius)
          )
        ];
      }),
      yAxisIndex: 1,
      name: this.translateService.instant('TRACK.COLUMN_NAMES.curve_radius'),
      symbol: 'none',
      lineStyle: { opacity: 0 },
      tooltip: { show: false }
    };
  }

  //Create station series overlay

  private createStationSeries(stationLabels: any[], yAxisMin: number): CartesianChartSeries {
    return {
      type: 'scatter',
      data: stationLabels.map(station => [station.value, yAxisMin]),
      xAxisIndex: 0,
      yAxisIndex: 0,
      symbolSize: CHART_CONSTANTS.STATION.SYMBOL_SIZE,
      symbol: 'triangle',
      silent: true,
      tooltip: { show: false },
      label: {
        show: true,
        position: 'bottom',
        formatter: (params: any) => stationLabels[params.dataIndex].name,
        rotate: CHART_CONSTANTS.STATION.LABEL_ROTATE,
        distance: CHART_CONSTANTS.STATION.LABEL_DISTANCE,
        fontSize: CHART_CONSTANTS.STATION.LABEL_FONT_SIZE,
        color: this.getThemeTextColor(),
        fontFamily: 'sans-serif'
      },
      z: 999
    } as CartesianChartSeries;
  }

  // Create default chart options with generic tooltip

  private createDefaultOptions(
    xData: any[],
    stationLabels: any[],
    trackDataSets?: TrackDataSet[],
    parameterInfo?: any,
    label1?: string,
    label2?: string,
    dataKey?: TrackParameterName
  ): EChartOption {
    const maxDataPosition = Math.max(...xData);
    const maxStationPosition = Math.max(...stationLabels.map(station => station.value));
    const maxPosition = Math.max(maxDataPosition, maxStationPosition);

    return {
      tooltip:
        trackDataSets && parameterInfo && label1 && dataKey
          ? this.buildGenericTooltip(trackDataSets, parameterInfo, label1, label2, dataKey)
          : {
              trigger: 'axis',
              axisPointer: { type: 'cross' }
            },
      xAxis: [
        {
          type: 'value',
          min: 0,
          max: maxPosition,
          axisLabel: {
            formatter: '{value} m',
            color: this.getThemeTextColor()
          },
          xAxisIndex: 0
        },
        {
          type: 'value',
          min: 0,
          max: maxPosition,
          axisLabel: { show: false },
          position: 'bottom',
          offset: 30,
          axisTick: { show: false },
          axisLine: { show: false }
        }
      ],
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
      grid: [
        {
          show: true,
          containLabel: true,
          bottom: CHART_CONSTANTS.GRID.BOTTOM_PADDING
        }
      ]
    };
  }

  // Calculate smart Y-axis minimum

  calculateSmartYAxisMin(...dataSeries: any[][]): number {
    let allDataValues: number[] = [];

    for (const series of dataSeries) {
      if (series && series.length > 0) {
        const seriesValues = series.map(point => point[1]);
        allDataValues = [...allDataValues, ...seriesValues];
      }
    }

    if (allDataValues.length === 0) return 0;
    const minDataValue = Math.min(...allDataValues);
    return Math.min(0, minDataValue);
  }

  //Build generic tooltip configuration - used by all chart types

  private buildGenericTooltip(
    trackDataSets: TrackDataSet[],
    parameterInfo: any,
    label1: string,
    label2: string | undefined,
    dataKey: TrackParameterName
  ): any {
    return {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: (params: any) => {
        if (!params || params.length === 0) return '';

        const first = params[0];
        const position = Array.isArray(first.value)
          ? first.value[0]
          : (first.axisValue ?? first.name);

        const dataPoint = trackDataSets.find(data => data.position === Number(position));

        let html = '<div>';
        html += `<strong>${this.translateService.instant('TRACK.CURVE.POSITION')}:</strong> ${Number(position).toFixed(0)}m<br/>`;

        if (dataKey === 'curve_data') {
          html += this.buildCurveTooltipContent(params[0], dataPoint);
        } else {
          html += this.buildGenericTooltipContent(params, parameterInfo, label1, label2);
        }

        html += '</div>';
        return html;
      }
    };
  }

  /**
   * Build curve-specific tooltip content
   * Note: Uses original radius from trackParameter1 (the input data)
   * instead of computing it from curvature to avoid rounding errors
   */
  private buildCurveTooltipContent(param: any, dataPoint?: TrackDataSet): string {
    const curvature = param.value[1]; // Displayed curvature (transformed from radius)

    // Use original radius from input data (trackParameter1)
    if (!dataPoint) {
      // Fallback: compute from curvature if dataPoint not found
      const radius = curvature > 0 ? 1 / curvature : CHART_CONSTANTS.CURVE.MAX_RADIUS;
      return `
        <strong>${this.translateService.instant('TRACK.CURVE.CURVATURE')}:</strong> ${curvature.toFixed(4)} m⁻¹<br/>
        <strong>${this.translateService.instant('TRACK.CURVE.RADIUS_LABEL')}:</strong> ${radius.toFixed(0)}m radius (computed)<br/>
      `;
    }

    // Primary path: use original radius from trackParameter1
    const originalRadius = dataPoint.trackParameter1;
    const radiusText = originalRadius === 0 ? 'Straight (∞ radius)' : `${originalRadius}m radius`;

    return `
      <strong>${this.translateService.instant('TRACK.CURVE.CURVATURE')}:</strong> ${curvature.toFixed(4)} m⁻¹<br/>
      <strong>${this.translateService.instant('TRACK.CURVE.RADIUS_LABEL')}:</strong> ${radiusText}<br/>
    `;
  }

  //Generic tooltip content

  private buildGenericTooltipContent(
    params: any[],
    parameterInfo: any,
    label1: string,
    label2?: string
  ): string {
    let html = '';
    for (const p of params) {
      const seriesName = p.seriesName ?? (p.seriesIndex === 1 && label2 ? label2 : label1);
      const val = Array.isArray(p.value) ? p.value[1] : p.value;
      const unit =
        p.seriesIndex === 1 && parameterInfo.trackParameter2
          ? parameterInfo.trackParameter2.unit
          : parameterInfo.trackParameter1.unit;
      const formattedVal =
        typeof val === 'number'
          ? Math.abs(val) < 1 && unit === 'm⁻¹'
            ? val.toFixed(4)
            : Number.isInteger(val)
              ? val.toString()
              : val.toFixed(2)
          : val;
      html += `<strong>${seriesName}:</strong> ${formattedVal} ${unit ?? ''}<br/>`;
    }
    return html;
  }

  // Get warning-aware item style

  getWarningAwareItemStyle(warningFlags: boolean[]): any {
    return {
      color: (params: any) => {
        const dataIndex = params?.dataIndex;
        if (dataIndex !== undefined && warningFlags[dataIndex]) {
          return this.settingsService.theme() === 'dark'
            ? 'rgba(255, 100, 80, 0.9)'
            : 'rgba(220, 50, 30, 0.85)';
        }
        return undefined;
      }
    };
  }

  // Get theme-aware text color (#fff for dark, #000 for light)

  private getThemeTextColor(): string {
    return this.settingsService.theme() === 'dark' ? '#fff' : '#000';
  }

  //Get symbol size based on warning status

  private getSymbolSize(warningFlags: boolean[]): ((val: any, params: any) => number) | number {
    if (warningFlags.some(flag => flag)) {
      // Return function if there are any warnings
      return (val: any, params: any) => {
        return params?.dataIndex !== undefined && warningFlags[params.dataIndex]
          ? CHART_CONSTANTS.POINT.SYMBOL_SIZE_WARNING
          : CHART_CONSTANTS.POINT.SYMBOL_SIZE;
      };
    }
    // Return constant if no warnings
    return CHART_CONSTANTS.POINT.SYMBOL_SIZE;
  }

  // Chart type helpers

  private isStepChart(dataKey: TrackParameterName): boolean {
    return [
      'maximum_speed_data',
      'curve_data',
      'curve_radius_data_graz',
      'tunnel_factor_data',
      'network_power_data'
    ].includes(dataKey);
  }

  private isBarChart(dataKey: TrackParameterName): boolean {
    return ['charging_station_data', 'hydrogen_refuel_station_data'].includes(dataKey);
  }

  // Get snap step for editing

  getSnapStep(dataKey: TrackParameterName): number {
    return SNAP_STEPS[dataKey] ?? 1;
  }
}
